import { useCallback, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { SliderContext } from '.';
var IsTouchingStatus;

(function (IsTouchingStatus) {
  IsTouchingStatus[IsTouchingStatus["Idle"] = 0] = "Idle";
  IsTouchingStatus[IsTouchingStatus["Start"] = 1] = "Start";
  IsTouchingStatus[IsTouchingStatus["Moving"] = 2] = "Moving";
})(IsTouchingStatus || (IsTouchingStatus = {}));

export var useSliderEvents = function useSliderEvents(_ref) {
  var getLinePosition = _ref.getLinePosition,
      lineRef = _ref.lineRef,
      valueGroup = _ref.valueGroup,
      setValueGroup = _ref.setValueGroup,
      setCommonIsTouching = _ref.setCommonIsTouching;

  var _useContext = useContext(SliderContext),
      disabled = _useContext.disabled,
      step = _useContext.step,
      min = _useContext.min,
      max = _useContext.max,
      type = _useContext.type,
      draggableTrackOnly = _useContext.draggableTrackOnly;

  var _useState = useState(IsTouchingStatus.Idle),
      isTouching = _useState[0],
      setIsTouching = _useState[1];

  var _useState2 = useState(0),
      touchStartPosition = _useState2[0],
      setTouchStartPosition = _useState2[1];

  var linePositionRef = useRef({
    length: 0,
    start: 0
  });
  var isHorizontal = /^horizontal/g.test(type);
  var validateValue = useCallback(function (value) {
    if (value >= min && value <= max) return value;
    return value <= (min + max) / 2 ? min : max;
  }, [min, max]); // 通过位置获取值

  var getValueFromPosition = useCallback(function (touchPosition) {
    var _linePositionRef$curr = linePositionRef.current,
        length = _linePositionRef$curr.length,
        start = _linePositionRef$curr.start;
    if (length === 0) return 0;
    var newWidth = isHorizontal ? touchPosition - start : length - touchPosition + start;

    if (newWidth < 0) {
      newWidth = 0;
    } else if (newWidth > length) {
      newWidth = length;
    }

    var newRealTimeValue = validateValue(Math.round(newWidth / length * (max - min) / step) * step + min);
    return newRealTimeValue;
  }, [isHorizontal, max, min]); // 设置不同的值，因为可以点击 line ，需要提前计算具体修改的是哪个值。

  var setCommonValue = useMemo(function () {
    var start = getValueFromPosition(touchStartPosition);

    if (!Array.isArray(valueGroup)) {
      if (isTouching) {
        setValueGroup(start);
        setCommonIsTouching(0);
        setIsTouching(IsTouchingStatus.Moving);
      }

      return function (realTimeValue) {
        setValueGroup(realTimeValue);
      };
    } else {
      var index = Math.abs(valueGroup[0] - start) < Math.abs(valueGroup[1] - start) ? 0 : 1;

      var handleValue = function handleValue(realTimeValue) {
        setValueGroup(function (passValueGroup) {
          if (typeof passValueGroup === 'number') {
            return [passValueGroup, realTimeValue];
          }

          var newValueGroup = [].concat(passValueGroup);
          newValueGroup[index] = realTimeValue;
          return newValueGroup;
        });
      };

      if (isTouching) {
        setCommonIsTouching(index);
        handleValue(start);
        setIsTouching(IsTouchingStatus.Moving);
      }

      return handleValue;
    }
  }, [touchStartPosition, getValueFromPosition]);
  useEffect(function () {
    var _lineRef$current;

    if (disabled) return;

    var handleTouchStart = function handleTouchStart(e) {
      if (draggableTrackOnly && e.target === lineRef.current) return;
      linePositionRef.current = getLinePosition();
      var _e$touches$ = e.touches[0],
          clientX = _e$touches$.clientX,
          clientY = _e$touches$.clientY;
      var touchPosition = isHorizontal ? clientX : clientY;
      setIsTouching(IsTouchingStatus.Start);
      setTouchStartPosition(touchPosition);
      e.stopPropagation();
      e.preventDefault();
    };

    (_lineRef$current = lineRef.current) == null ? void 0 : _lineRef$current.addEventListener('touchstart', handleTouchStart);
    return function () {
      var _lineRef$current2;

      (_lineRef$current2 = lineRef.current) == null ? void 0 : _lineRef$current2.removeEventListener('touchstart', handleTouchStart);
    };
  }, [disabled, isHorizontal, draggableTrackOnly]);
  useEffect(function () {
    var _lineRef$current3;

    if (disabled) return;

    var handleTouchMove = function handleTouchMove(e) {
      if (isTouching === 2) {
        var _e$touches$2 = e.touches[0],
            clientX = _e$touches$2.clientX,
            clientY = _e$touches$2.clientY;
        var touchPosition = isHorizontal ? clientX : clientY;
        setCommonValue(getValueFromPosition(touchPosition));
      }

      e.stopPropagation();
      e.cancelable && e.preventDefault();
    };

    (_lineRef$current3 = lineRef.current) == null ? void 0 : _lineRef$current3.addEventListener('touchmove', handleTouchMove);
    return function () {
      var _lineRef$current4;

      (_lineRef$current4 = lineRef.current) == null ? void 0 : _lineRef$current4.removeEventListener('touchmove', handleTouchMove);
    };
  }, [disabled, isHorizontal, isTouching]);
  useEffect(function () {
    var _lineRef$current5, _lineRef$current6;

    if (disabled) return;

    var handleTouchEnd = function handleTouchEnd(e) {
      setIsTouching(IsTouchingStatus.Idle);
      setCommonIsTouching(-1);
      e.stopPropagation();
      e.preventDefault();
    };

    (_lineRef$current5 = lineRef.current) == null ? void 0 : _lineRef$current5.addEventListener('touchend', handleTouchEnd);
    (_lineRef$current6 = lineRef.current) == null ? void 0 : _lineRef$current6.addEventListener('touchcancel', handleTouchEnd);
    return function () {
      var _lineRef$current7, _lineRef$current8;

      (_lineRef$current7 = lineRef.current) == null ? void 0 : _lineRef$current7.removeEventListener('touchend', handleTouchEnd);
      (_lineRef$current8 = lineRef.current) == null ? void 0 : _lineRef$current8.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [disabled]);
  return {
    isTouching: isTouching
  };
};